import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SpecialistReview } from '@mindly/ui-components';
import { SpecialistReviewStore } from './types';
import {
  createReview,
  fetchClientSpecialistsReviews,
  fetchRatedSpecialists,
  fetchReviews,
  updateReview,
} from './thunk';
import { REVIEW_COUNT_PER_PAGE } from './constants';

const initialState: SpecialistReviewStore = {
  reviews: {},
  clientSpecialistsReviews: {},
  reviewedSpecialists: null,
  loading: 'idle',
};

export const specialistReviewsSlice = createSlice({
  name: 'specialistReviews',
  initialState,
  reducers: {
    resetReview: () => initialState,
    resetSpecialistReview: (state, action: PayloadAction<string>) => {
      if (state.reviews[action.payload]) {
        state.reviews[action.payload].data = [];
      }
    },
    resetOffset: (state, action: PayloadAction<string>) => {
      if (state.reviews[action.payload]) {
        state.reviews[action.payload].offset = 0;
        state.reviews[action.payload].isInfiniteDisabled = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchReviews.fulfilled, (state, action) => {
      const actionPayloadData = action?.payload?.data;
      const specialistID = actionPayloadData[0]?.specialist_id ?? action.payload.specialistID;

      if (actionPayloadData?.length === 0) {
        if (state?.reviews?.[specialistID]?.isInfiniteDisabled) state.reviews[specialistID].isInfiniteDisabled = true;
        return;
      }

      if (state.reviews[specialistID])
        state.reviews[specialistID].data = [
          ...new Map(
            [...state.reviews[specialistID].data, ...actionPayloadData].map((item) => [item.id, item])
          ).values(),
        ];
      else
        state.reviews[specialistID] = {
          data: actionPayloadData,
          limit: REVIEW_COUNT_PER_PAGE,
          offset: 0,
          isInfiniteDisabled: false,
        };

      state.reviews[specialistID].offset = state.reviews[specialistID].offset + state.reviews[specialistID].limit;
    });
    builder.addCase(fetchClientSpecialistsReviews.fulfilled, (state, { payload }) => {
      state.clientSpecialistsReviews = {
        ...state.clientSpecialistsReviews,
        ...payload.reduce((acc: { [key: string]: SpecialistReview }, review: SpecialistReview) => {
          acc[review.specialist_id] = review;
          return acc;
        }, {}),
      };
    });
    builder.addCase(fetchRatedSpecialists.fulfilled, (state, { payload }) => {
      state.reviewedSpecialists = payload;
    });
    builder.addCase(createReview.fulfilled, (state, action) => {
      state.reviewedSpecialists = [...(state.reviewedSpecialists ?? []), action.payload.specialist_id];
    });
    builder.addCase(updateReview.fulfilled, (state, action) => {
      state.reviews[action.payload.specialist_id].data = state.reviews[action.payload.specialist_id].data.filter(
        (review) => review.id !== action.payload.id
      );
    });
  },
});

export default specialistReviewsSlice.reducer;

export const { resetReview, resetOffset, resetSpecialistReview } = specialistReviewsSlice.actions;
